import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../hooks/useCart';
import { useToast } from '../hooks/useToast';
import Spinner from '../components/Spinner';
import Toast from '../components/Toast';
import ImageWithLoader from '../components/ImageWithLoader';
import './ProductPage.css';

function ProductPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { toast, showToast, closeToast } = useToast();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isInWishlist, setIsInWishlist] = useState(false);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const [featuredProducts, setFeaturedProducts] = useState([]);

  const parseVariation = (variation) => {
    const combinedValue = variation.size || variation.name || '';
    const parts = combinedValue.split(/\s*\/\s*/).filter(Boolean);
    const parsedColor = variation.color || (parts.length > 1 ? parts.slice(0, -1).join(' / ') : '');
    const parsedSize = parts.length > 1 ? parts[parts.length - 1] : variation.size;

    return {
      ...variation,
      color: parsedColor,
      size: parsedSize,
      optionLabel: combinedValue
    };
  };

  const getNormalizedVariations = (sourceProduct = product) => {
    if (!sourceProduct?.variations) return [];
    return sourceProduct.variations.map(parseVariation);
  };

  const imageMatchesVariantId = (imageUrl, variantId) => {
    if (!imageUrl || !variantId) return false;
    const id = String(variantId);
    return imageUrl.includes(`/${id}/`) || imageUrl.includes(`/${id}?`) || imageUrl.endsWith(`/${id}`);
  };

  const getImageIndexForSelection = (sourceProduct, color, size) => {
    const images = sourceProduct?.images || [];
    const normalizedVariations = getNormalizedVariations(sourceProduct);
    if (!images.length || !normalizedVariations.length) return -1;

    const exactVariation = normalizedVariations.find(
      v => v.color === color && v.size === size
    );

    if (exactVariation?.printify_variant_id) {
      const exactIndex = images.findIndex(imageUrl =>
        imageMatchesVariantId(imageUrl, exactVariation.printify_variant_id)
      );

      if (exactIndex !== -1) return exactIndex;
    }

    const colorVariantIds = normalizedVariations
      .filter(v => v.color === color && v.printify_variant_id)
      .map(v => v.printify_variant_id);

    return images.findIndex(imageUrl =>
      colorVariantIds.some(variantId => imageMatchesVariantId(imageUrl, variantId))
    );
  };

  const uniqueValues = (values) => [...new Set(values.filter(Boolean))];

  const sizeSortOrder = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'];
  const sortSizes = (sizes) => [...sizes].sort((a, b) => {
    const aIndex = sizeSortOrder.indexOf(String(a).toUpperCase());
    const bIndex = sizeSortOrder.indexOf(String(b).toUpperCase());

    if (aIndex !== -1 || bIndex !== -1) {
      return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
    }

    return String(a).localeCompare(String(b), undefined, { numeric: true });
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
    fetchProduct();
    fetchFeaturedProducts();
    checkWishlistStatus();
  }, [slug]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`/api/products/${slug}`);
      setProduct(response.data);

      // Set default selections
      if (response.data.variations && response.data.variations.length > 0) {
        const normalizedVariations = getNormalizedVariations(response.data);
        const colors = uniqueValues(normalizedVariations.map(v => v.color));
        const sizes = sortSizes(uniqueValues(normalizedVariations.map(v => v.size)));
        const defaultColor = colors[0] || '';
        const defaultSize = sizes[0] || '';

        if (defaultColor) setSelectedColor(defaultColor);
        if (defaultSize) setSelectedSize(defaultSize);

        const defaultImageIndex = getImageIndexForSelection(response.data, defaultColor, defaultSize);
        setCurrentImageIndex(defaultImageIndex === -1 ? 0 : defaultImageIndex);
      }

      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const fetchFeaturedProducts = async () => {
    try {
      const response = await axios.get('/api/products/featured');
      setFeaturedProducts(response.data);
    } catch (err) {
      setFeaturedProducts([]);
    }
  };

  const getAvailableColors = () => {
    return uniqueValues(getNormalizedVariations().map(v => v.color));
  };

  const getAvailableSizes = () => {
    return sortSizes(uniqueValues(getNormalizedVariations().map(v => v.size)));
  };

  const isColorAvailable = (color) => {
    return getNormalizedVariations().some(v => v.color === color && (!selectedSize || v.size === selectedSize));
  };

  const isSizeAvailable = (size) => {
    return getNormalizedVariations().some(v => v.size === size && (!selectedColor || v.color === selectedColor));
  };

  const getSelectedVariation = () => {
    const normalizedVariations = getNormalizedVariations();
    if (!normalizedVariations.length) return null;
    return normalizedVariations.find(
      v => v.color === selectedColor && v.size === selectedSize
    );
  };

  const handleColorSelect = (color) => {
    const normalizedVariations = getNormalizedVariations();
    const currentSizeStillAvailable = normalizedVariations.some(
      v => v.color === color && v.size === selectedSize
    );
    let nextSize = selectedSize;

    if (!currentSizeStillAvailable) {
      nextSize = sortSizes(uniqueValues(
        normalizedVariations
          .filter(v => v.color === color)
          .map(v => v.size)
      ))[0];

      setSelectedSize(nextSize || '');
    }

    setSelectedColor(color);

    const imageIndex = getImageIndexForSelection(product, color, nextSize);
    if (imageIndex !== -1) {
      setCurrentImageIndex(imageIndex);
    }
  };

  const handleSizeSelect = (size) => {
    const normalizedVariations = getNormalizedVariations();
    const currentColorStillAvailable = normalizedVariations.some(
      v => v.size === size && v.color === selectedColor
    );
    let nextColor = selectedColor;

    if (!currentColorStillAvailable) {
      nextColor = uniqueValues(
        normalizedVariations
          .filter(v => v.size === size)
          .map(v => v.color)
      )[0];

      setSelectedColor(nextColor || '');
    }

    setSelectedSize(size);

    const imageIndex = getImageIndexForSelection(product, nextColor, size);
    if (imageIndex !== -1) {
      setCurrentImageIndex(imageIndex);
    }
  };

  const colorSwatchValue = (color) => {
    const colorMap = {
      black: '#111111',
      white: '#ffffff',
      red: '#d63b32',
      blue: '#2f6fb7',
      navy: '#1e2a44',
      grey: '#9b9b9b',
      gray: '#9b9b9b',
      charcoal: '#3f4447',
      green: '#2f7d46',
      'irish green': '#169b62',
      'light blue': '#8ecae6',
      orange: '#f47c20',
      gold: '#d6a329',
      purple: '#6f4aa8',
      pink: '#e78cae',
      brown: '#7a4b32',
      yellow: '#f2cf3a'
    };

    return colorMap[String(color).trim().toLowerCase()] || '#d9d9d9';
  };

  const handleAddToCart = () => {
    const variation = getSelectedVariation();

    if (product.variations && product.variations.length > 0 && !variation) {
      showToast('Please select a color and size', 'warning');
      return false;
    }

    addToCart(product, variation, quantity);
    showToast('Added to cart!', 'success');
    return true;
  };

  const handleBuyNow = () => {
    if (handleAddToCart()) {
      navigate('/cart');
    }
  };

  const checkWishlistStatus = async () => {
    const token = localStorage.getItem('authToken');
    if (!token || !product) return;

    try {
      const response = await axios.get('/api/auth/wishlist', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const inWishlist = response.data.some(item => item.product_id === product.id);
      setIsInWishlist(inWishlist);
    } catch (error) {
      // User might not be logged in, that's okay
    }
  };

  const handleToggleWishlist = async () => {
    const token = localStorage.getItem('authToken');

    if (!token) {
      showToast('Please log in to add items to your wishlist', 'info');
      setTimeout(() => navigate('/login'), 1500);
      return;
    }

    setWishlistLoading(true);

    try {
      if (isInWishlist) {
        // Remove from wishlist
        await axios.delete(`/api/auth/wishlist/${product.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsInWishlist(false);
        showToast('Removed from wishlist', 'success');
      } else {
        // Add to wishlist
        await axios.post(`/api/auth/wishlist/${product.id}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsInWishlist(true);
        showToast('Added to wishlist!', 'success');
      }
    } catch (error) {
      showToast('Failed to update wishlist: ' + (error.response?.data?.error || error.message), 'error');
    } finally {
      setWishlistLoading(false);
    }
  };

  // Re-check wishlist status when product changes
  useEffect(() => {
    if (product) {
      checkWishlistStatus();
    }
  }, [product]);

  if (loading) return <Spinner size="large" text="Loading product..." />;
  if (error) return <div className="error">Error: {error}</div>;
  if (!product) return <div className="error">Product not found</div>;

  const selectedVariation = getSelectedVariation();
  const displayPrice = selectedVariation?.price || product.price;
  const availableColors = getAvailableColors();
  const availableSizes = getAvailableSizes();
  const hasVariations = product.variations && product.variations.length > 0;
  const variationSelectionMissing = hasVariations && !selectedVariation;
  const selectionLabel = selectedVariation
    ? [selectedVariation.color, selectedVariation.size].filter(Boolean).join(' / ')
    : '';
  const relatedFeaturedProducts = featuredProducts
    .filter(featuredProduct => featuredProduct.id !== product.id)
    .slice(0, 8);

  const scrollFeaturedCarousel = (direction) => {
    const carousel = document.querySelector('.featured-products-carousel');
    if (!carousel) return;

    carousel.scrollBy({
      left: direction * Math.round(carousel.clientWidth * 0.85),
      behavior: 'smooth'
    });
  };

  return (
    <div className="product-page">
      {toast && <Toast message={toast.message} type={toast.type} onClose={closeToast} />}
      <div className="product-container">
        <div className="product-images">
          <div className="main-image">
            {product.images && product.images.length > 0 ? (
              <img
                src={product.images[currentImageIndex]}
                alt={product.name}
              />
            ) : (
              <div className="no-image">No image available</div>
            )}
          </div>

          {product.images && product.images.length > 1 && (
            <div className="image-thumbnails">
              {product.images.map((image, index) => (
                <div
                  key={index}
                  className={`thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                  onClick={() => setCurrentImageIndex(index)}
                >
                  <img src={image} alt={`${product.name} ${index + 1}`} />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="product-details">
          <h1>{product.name}</h1>

          <div className="price-section">
            <span className="price">${displayPrice?.toFixed(2)}</span>
            {product.stock_status === 'instock' ? (
              <span className="in-stock">In Stock</span>
            ) : (
              <span className="out-of-stock">Out of Stock</span>
            )}
          </div>

          {product.short_description && (
            <p className="short-description">{product.short_description}</p>
          )}

          {hasVariations && (
            <div className="variations">
              {availableColors.length > 0 && (
                <div className="variation-group">
                  <div className="variation-heading">
                    <span>Color</span>
                    {selectedColor && <strong>{selectedColor}</strong>}
                  </div>
                  <div className="color-options" role="listbox" aria-label="Choose color">
                    {availableColors.map(color => {
                      const available = isColorAvailable(color);

                      return (
                        <button
                          key={color}
                          type="button"
                          className={`color-option ${selectedColor === color ? 'active' : ''}`}
                          onClick={() => handleColorSelect(color)}
                          disabled={!available}
                          aria-label={`Select ${color}`}
                          aria-pressed={selectedColor === color}
                          title={color}
                        >
                          <span
                            className="color-swatch"
                            style={{ backgroundColor: colorSwatchValue(color) }}
                          />
                          <span>{color}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="variation-group">
                <div className="variation-heading">
                  <span>Size</span>
                  {selectedSize && <strong>{selectedSize}</strong>}
                </div>
                <div className="size-options" role="listbox" aria-label="Choose size">
                  {availableSizes.map(size => {
                    const available = isSizeAvailable(size);

                    return (
                      <button
                        key={size}
                        type="button"
                        className={`size-option ${selectedSize === size ? 'active' : ''}`}
                        onClick={() => handleSizeSelect(size)}
                        disabled={!available}
                        aria-pressed={selectedSize === size}
                      >
                        {size}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="selected-variation-summary">
                <span>Selected:</span>
                <strong>
                  {selectionLabel || `Choose ${availableColors.length > 0 ? 'a color and size' : 'a size'}`}
                </strong>
              </div>
            </div>
          )}

          <div className="quantity-section">
            <label>Quantity:</label>
            <div className="quantity-control">
              <button onClick={() => setQuantity(Math.max(1, quantity - 1))}>−</button>
              <input
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                min="1"
              />
              <button onClick={() => setQuantity(quantity + 1)}>+</button>
            </div>
          </div>

          <div className="action-buttons">
            <button
              className="btn btn-primary"
              onClick={handleAddToCart}
              disabled={product.stock_status !== 'instock' || variationSelectionMissing}
            >
              Add to Cart
            </button>
            <button
              className="btn btn-secondary"
              onClick={handleBuyNow}
              disabled={product.stock_status !== 'instock' || variationSelectionMissing}
            >
              Buy Now
            </button>
            <button
              className={`btn btn-wishlist ${isInWishlist ? 'in-wishlist' : ''}`}
              onClick={handleToggleWishlist}
              disabled={wishlistLoading}
              title={isInWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
            >
              {isInWishlist ? 'Remove from Wishlist' : 'Add to Wishlist'}
            </button>
          </div>

          {product.description && (
            <div className="description">
              <h3>Description</h3>
              <div dangerouslySetInnerHTML={{
                __html: product.description
                  .replace(/\\n/g, '<br />')  // Replace literal \n with <br />
                  .replace(/\n/g, '<br />')   // Replace actual newlines with <br />
              }} />
            </div>
          )}
        </div>
      </div>

      {relatedFeaturedProducts.length > 0 && (
        <section className="featured-products-section">
          <div className="featured-products-header">
            <h2>Featured Shirts</h2>
            <div className="featured-carousel-controls">
              <button
                type="button"
                className="featured-carousel-arrow"
                onClick={() => scrollFeaturedCarousel(-1)}
                aria-label="Scroll featured products left"
              >
                ‹
              </button>
              <button
                type="button"
                className="featured-carousel-arrow"
                onClick={() => scrollFeaturedCarousel(1)}
                aria-label="Scroll featured products right"
              >
                ›
              </button>
            </div>
          </div>
          <div className="featured-products-carousel-shell">
            <div className="featured-products-carousel" aria-label="Featured products">
              {relatedFeaturedProducts.map(featuredProduct => {
                const imageUrl = featuredProduct.images && featuredProduct.images.length > 0
                  ? featuredProduct.images[0]
                  : '/placeholder.png';

                return (
                  <Link
                    key={featuredProduct.id}
                    to={`/product/${featuredProduct.slug}`}
                    className="featured-product-card"
                  >
                    <div className="featured-product-image">
                      <ImageWithLoader src={imageUrl} alt={featuredProduct.name} />
                    </div>
                    <div className="featured-product-info">
                      <h3>{featuredProduct.name}</h3>
                      <span>${featuredProduct.price?.toFixed(2)}</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

export default ProductPage;
